import React, { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  VStack,
  Text,
  Checkbox,
  CheckboxGroup,
  FormErrorMessage,
  useToast,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";

interface Permission {
  name: string;
  key: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  permissions: string[];
}

const permissionsList: Permission[] = [
  { name: "User management", key: "user" },
  { name: "Restaurant management", key: "restaurant" },
  { name: "Community management", key: "community" },
  { name: "Booking management", key: "booking" },
  { name: "Marketing management", key: "marketing" },
  { name: "Gift Card", key: "gift" },
  { name: "Help Centre", key: "help" },
  { name: "FAQ", key: "faq" },
  { name: "Notifications", key: "notification" },
  { name: "Credit management", key: "credit" },
];

const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  phoneNumber: Yup.number().required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters long")
    .required("Password is required"),
});

interface AddSubAdminsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  getData: () => void;
  editData?: any;
}

const AddSubAdminsDrawer: React.FC<AddSubAdminsDrawerProps> = ({
  isOpen,
  onClose,
  getData,
  editData,
}) => {
  // console.log(editData);
  const toast = useToast();

  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const initialValues: FormValues = {
    firstName: editData ? editData.firstName : "",
    lastName: editData ? editData.lastName : "",
    email: editData ? editData.email : "",
    phoneNumber: editData ? editData.phoneNumber : "",
    password: editData ? editData.password : "", // Note: Consider security implications of handling passwords
    permissions: editData
      ? editData?.permits?.map((permit: any) => permit.resource)
      : [],
  };
  const submitForm = (values: FormValues, actions: any) => {
    const permits = permissionsList
      .filter((permission) => values.permissions.includes(permission.key))
      .map((permission) => ({
        resource: permission.key,
        action: ["write", "delete"],
      }));

    if (editData) {
      axios({
        method: "patch",
        url: `${process.env.REACT_APP_BASE_URL}/admin/subadmin/${editData._id}`,
        data: {
          ...values,
          permits,
        },
      })
        .then((response) => {
          actions.setSubmitting(false);
          onClose(); // Optionally close the drawer on submit
          getData(); // Optionally refresh the data after submit
          toast({
            title: "Sub-Admin updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to update Sub-Admin",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          console.error("API Error:", error);
        });
    } else {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_BASE_URL}/admin/subadmin`,
        data: {
          ...values,
          permits,
        },
      })
        .then((response) => {
          actions.setSubmitting(false);
          onClose(); // Optionally close the drawer on submit
          getData(); // Optionally refresh the data after submit
          toast({
            title: "Sub-Admin added successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to add Sub-Admin",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          actions.setSubmitting(false);
          console.error("API Error:", error);
        });
    }
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitForm}
    >
      {(props) => (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader borderBottomWidth="1px" bg="#FFF1F1" color="#000">
              {editData ? "Edit Sub-admin" : "Add Sub-Admin"}
            </DrawerHeader>
            <Form>
              <DrawerBody>
                <VStack
                  spacing={6}
                  pt={4}
                  height="65vh"
                  align="stretch"
                  overflowY={"scroll"}
                  paddingX={3}
                >
                  <Field name="firstName">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.firstName && form.touched.firstName
                        }
                      >
                        <FormLabel>First Name</FormLabel>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter first name"
                        />
                        <FormErrorMessage>
                          {form.errors.firstName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="lastName">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.lastName && form.touched.lastName
                        }
                      >
                        <FormLabel>Last Name</FormLabel>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter last name"
                        />
                        <FormErrorMessage>
                          {form.errors.lastName}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="phoneNumber">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.phoneNumber && form.touched.phoneNumber
                        }
                      >
                        <FormLabel>Phone Number</FormLabel>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter phone number"
                        />
                        <FormErrorMessage>
                          {form.errors.phoneNumber}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="email">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={!!form.errors.email && form.touched.email}
                      >
                        <FormLabel>Email</FormLabel>
                        <Input
                          {...field}
                          type="email"
                          placeholder="Enter email"
                          autoComplete="new-email"
                        />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password">
                    {({ field, form }: { field: any; form: any }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.password && form.touched.password
                        }
                      >
                        <FormLabel>Password</FormLabel>
                        <InputGroup size="md">
                          <Input
                            {...field}
                            type={show ? "text" : "password"}
                            placeholder="Enter password"
                            autoComplete="new-password"
                          />
                          <InputRightElement width="4.5rem">
                            <Box cursor={"pointer"} onClick={handleClick}>
                              {show ? <IoEyeOffOutline /> : <IoEyeOutline />}
                            </Box>
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Box pt={4}>
                    <Text fontWeight="500" mb={4}>
                      Access permissions
                    </Text>
                    <CheckboxGroup
                      colorScheme="red"
                      value={props.values.permissions}
                      onChange={(val) =>
                        props.setFieldValue("permissions", val)
                      }
                    >
                      <SimpleGrid columns={2} spacing={2}>
                        {permissionsList.map(({ name, key }) => (
                          <Checkbox key={key} value={key}>
                            {name}
                          </Checkbox>
                        ))}
                      </SimpleGrid>
                    </CheckboxGroup>
                  </Box>
                </VStack>
              </DrawerBody>

              <DrawerFooter borderTopWidth="1px">
                <Button variant="outline" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={props.isSubmitting}
                  type="submit"
                >
                  {editData ? "Edit Sub-admin" : "Add Sub-Admin"}
                </Button>
              </DrawerFooter>
            </Form>
          </DrawerContent>
        </Drawer>
      )}
    </Formik>
  );
};

export default AddSubAdminsDrawer;
